import type { GetServerSideProps } from 'next';

import { getLocaleFromParams } from '@/utils/locale';

import { APOLLO_CLIENTS, initializeApollo } from '@/graphql/client';
import { fetchCmsPageDataWithExtraData } from '@/integrations/contentful/api';
import { getLocalesConfiguration } from '@/lib/configuration/api';
import { LocaleConfiguration } from '@/lib/configuration/configuration';
import { getGqlRegionFromLocale, getRegionFromLocale } from '@/lib/regions';
import { GET_WIDGET_CONFIG_QUERY } from '@/lib/wholesaler-widget/configuration';
import { RetailerWidgetView } from '@/views/RetailerWidgetView/RetailerWidgetView';

export const getServerSideProps: GetServerSideProps = async (context) => {
  const { lang, retailerId: rawRetailerId } = context.params || {};
  const { preview, tab } = context.query || {};

  const isPreview = preview === 'true' || preview === '1';

  if (!rawRetailerId || typeof rawRetailerId !== 'string') {
    return {
      notFound: true,
    };
  }

  const widgetConfig = await (async () => {
    try {
      const client = initializeApollo(null, false);
      const { data } = await client.query({
        query: GET_WIDGET_CONFIG_QUERY,
        variables: { code: rawRetailerId, preview: isPreview },
        fetchPolicy: 'no-cache',
        context: { client: APOLLO_CLIENTS.posManagerApp },
      });
      return data?.widgetConfig?.enabled ? data.widgetConfig : null;
    } catch (error) {
      console.error('Error fetching widget configuration:', error);
      return null;
    }
  })();

  if (!widgetConfig) {
    return { notFound: true };
  }

  const retailerId = widgetConfig.externalReference;
  const multiDoors = widgetConfig.multiDoors;
  const localeConfiguration = LocaleConfiguration.getInstance();
  const localesConfig = await getLocalesConfiguration();
  localeConfiguration.setStates(localesConfig);

  const routerLocale = getLocaleFromParams(context?.params);
  const { channel, languageCode } = getGqlRegionFromLocale(routerLocale);
  const { country, locale } = getRegionFromLocale(routerLocale);
  const countryCode = country.slug;

  const { cmsDefaultLayoutData } = await fetchCmsPageDataWithExtraData(
    'integrator/register',
    locale.locale,
    languageCode,
    channel,
    countryCode,
    context.draftMode,
  );

  return {
    props: {
      retailerId,
      multiDoors,
      tab: tab === 'new' ? 'new' : 'selection',
      lang: typeof lang === 'string' ? lang : 'us-en',
      cmsDefaultLayoutData,
      isPreview,
      locale: routerLocale,
      localeConfiguration: localeConfiguration.getStates(),
    },
  };
};

export default RetailerWidgetView;
